import { ICONS } from './icons.jsx';

// The one way to put an icon on screen. Replaces the emoji that used to sit in
// nav rows, tab strips and page headers — see docs/ICON_SYSTEM.md.
//
// Two treatments, and the rule for choosing is deliberately easy to review:
// IF IT APPEARS IN A LIST, IT'S MONOLINE; IF IT APPEARS ONCE, IT CAN BE A CHIP.
//
//   <Icon name="goals" />                          monoline — sidebar, tab bar,
//                                                  admin tabs, table rows, tile
//                                                  headers, buttons. Inherits
//                                                  the row's colour, so it goes
//                                                  muted -> brand with it.
//   <Icon name="goals" chip family="insight" />    chip — page headers, empty
//                                                  states, wizard steps. One
//                                                  chip anchors a screen;
//                                                  fourteen is wallpaper.
//
// `family` tints the chip and is one of insight | engage | ops | money | admin.
// Each tint is an existing Pulse token (--ai, --brand, --warn, --success,
// --muted-2), so the chip palette is the design system rather than a new one.
// Monoline ignores `family` on purpose: it must never set its own colour.
export default function Icon({
  name,
  size = 17,
  chip = false,
  family,
  title,
  style,
  className,
}) {
  const art = ICONS[name];
  if (!art) {
    // A typo shouldn't blank a nav row. Warn loudly in dev, render nothing.
    if (import.meta.env?.DEV) console.warn(`<Icon> unknown name: "${name}"`);
    return null;
  }

  const svg = (
    <svg
      width={chip ? Math.round(size * 0.72) : size}
      height={chip ? Math.round(size * 0.72) : size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      // The text label next to an icon already says what it is. Announcing the
      // icon too is what made every emoji nav row read twice.
      aria-hidden={title ? undefined : 'true'}
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      // Lets CSS give a glyph its own hover behaviour — the bell rings, the
      // target's rings ping. See the icon micro-interactions in index.css.
      data-icon={name}
      className={chip ? undefined : className}
      style={chip ? undefined : { flexShrink: 0, display: 'block', ...style }}
    >
      {title ? <title>{title}</title> : null}
      {art}
    </svg>
  );

  if (!chip) return svg;

  return (
    <span
      className={`icon-chip${className ? ` ${className}` : ''}`}
      data-family={family || undefined}
      style={{ width: size, height: size, ...style }}
    >
      {svg}
    </span>
  );
}
